/**
 * OAuth Service for Google and Facebook Authentication
 * Handles OAuth flow and user data extraction
 */

// Google OAuth Configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Facebook OAuth Configuration
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';

/**
 * Initialize Google OAuth
 */
export const initializeGoogleOAuth = () => {
  return new Promise((resolve, reject) => {
    // Check if Google script is already loaded
    if (window.google && window.google.accounts) {
      resolve();
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
        });
        resolve();
      } else {
        reject(new Error('Google Identity Services failed to load'));
      }
    };
    script.onerror = () => {
      reject(new Error('Failed to load Google Identity Services script'));
    };
    document.head.appendChild(script);
  });
};

/**
 * Initialize Facebook SDK
 */
export const initializeFacebookSDK = () => {
  return new Promise((resolve, reject) => {
    // Check if Facebook SDK is already loaded
    if (window.FB) {
      resolve();
      return;
    }

    // Load Facebook SDK script
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      // Wait a bit for FB to be available
      const checkFB = setInterval(() => {
        if (window.FB) {
          clearInterval(checkFB);
          window.FB.init({
            appId: FACEBOOK_APP_ID,
            cookie: true,
            xfbml: true,
            version: 'v18.0',
          });
          resolve();
        }
      }, 50);

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkFB);
        if (!window.FB) {
          reject(new Error('Facebook SDK failed to load'));
        }
      }, 5000);
    };
    script.onerror = () => {
      reject(new Error('Failed to load Facebook SDK script'));
    };
    document.head.appendChild(script);
  });
};

/**
 * Sign in with Google
 */
export const signInWithGoogle = () => {
  return new Promise(async (resolve, reject) => {
    try {
      await initializeGoogleOAuth();

      if (!GOOGLE_CLIENT_ID) {
        reject(new Error('Google Client ID is not configured'));
        return;
      }

      // Use Google OAuth 2.0 token client
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'email profile',
        callback: async (response) => {
          if (response.error) {
            reject(new Error(response.error));
            return;
          }

          try {
            // Get user info using the access token
            const userInfoResponse = await fetch(
              `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`
            );
            const userInfo = await userInfoResponse.json();

            if (userInfo.error) {
              reject(new Error(userInfo.error.message || 'Failed to get user info'));
              return;
            }

            // Format user data for backend
            const userData = {
              provider: 'google',
              provider_id: userInfo.id,
              email: userInfo.email,
              first_name: userInfo.given_name || '',
              last_name: userInfo.family_name || '',
              profile_image_url: userInfo.picture || null,
              access_token: response.access_token,
            };

            resolve(userData);
          } catch (error) {
            reject(error);
          }
        },
      });

      // Request access token
      tokenClient.requestAccessToken();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Sign in with Facebook
 */
export const signInWithFacebook = () => {
  return new Promise(async (resolve, reject) => {
    try {
      await initializeFacebookSDK();

      if (!FACEBOOK_APP_ID) {
        reject(new Error('Facebook App ID is not configured'));
        return;
      }

      // Wait for FB to be ready
      if (!window.FB) {
        reject(new Error('Facebook SDK is not loaded'));
        return;
      }

      window.FB.login(
        (response) => {
          if (response.authResponse) {
            try {
              // Get user info
              window.FB.api(
                '/me',
                { fields: 'id,name,email,picture' },
                (userInfo) => {
                  if (userInfo.error) {
                    reject(new Error(userInfo.error.message || 'Failed to get user info'));
                    return;
                  }

                  // Parse name into first and last name
                  const nameParts = (userInfo.name || '').split(' ');
                  const first_name = nameParts[0] || '';
                  const last_name = nameParts.slice(1).join(' ') || '';

                  // Format user data for backend
                  const userData = {
                    provider: 'facebook',
                    provider_id: userInfo.id,
                    email: userInfo.email || '',
                    first_name: first_name,
                    last_name: last_name,
                    profile_image_url: userInfo.picture?.data?.url || null,
                    access_token: response.authResponse.accessToken,
                  };

                  resolve(userData);
                }
              );
            } catch (error) {
              reject(error);
            }
          } else {
            if (response.status === 'not_authorized') {
              reject(new Error('Facebook login was not authorized'));
            } else {
              reject(new Error('Facebook login was cancelled or failed'));
            }
          }
        },
        { scope: 'email,public_profile' }
      );
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Check if OAuth providers are configured
 */
export const isOAuthConfigured = () => {
  return {
    google: !!GOOGLE_CLIENT_ID,
    facebook: !!FACEBOOK_APP_ID,
  };
};

